import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import './PMCalibrationTracker.css';

const SmartAssignModal = ({
  isOpen,
  onClose,
  assets,
  onExecute,
  stream,
  setStream
}) => {
  const [form, setForm] = useState({
    equipmentType: '',
    reportType: 'pm',
    reportFile: null,
    reportPreview: '',
    reportPages: [],
    matchedAssets: [],
    assignToAll: false,
    // For 'both' report type
    pmReportFile: null,
    pmReportPreview: '',
    calReportFile: null,
    calReportPreview: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [visibleAssets, setVisibleAssets] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState(new Set()); // Track selected asset IDs
  const [cameraPreview, setCameraPreview] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraMode, setCameraMode] = useState(null); // 'pm', 'cal', or null
  const [cameraStarting, setCameraStarting] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const getAssetKey = useCallback((asset) => {
    if (!asset) return '';
    const raw = asset.id ?? asset.assetId ?? asset.serialNo ?? '';
    return String(raw).trim();
  }, []);

  // Reset form when modal opens - with lazy loading
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      // Use setTimeout to allow UI to render loading state first
      const timer = setTimeout(() => {
        setForm({
          equipmentType: '',
          reportType: 'pm',
          reportFile: null,
          reportPreview: '',
          reportPages: [],
          matchedAssets: [],
          assignToAll: false,
          pmReportFile: null,
          pmReportPreview: '',
          calReportFile: null,
          calReportPreview: ''
        });
        setSelectedAssetIds(new Set()); // Reset selected assets
        setIsLoading(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleFileChange = useCallback((file, type = 'single') => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload only image files (JPG, PNG) or PDF files');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should not exceed 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'pm') {
        setForm(prev => ({
          ...prev,
          pmReportFile: file,
          pmReportPreview: e.target.result
        }));
      } else if (type === 'cal') {
        setForm(prev => ({
          ...prev,
          calReportFile: file,
          calReportPreview: e.target.result
        }));
      } else {
        setForm(prev => ({
          ...prev,
          reportFile: file,
          reportPreview: e.target.result,
          reportPages: file.type.startsWith('image/') ? [e.target.result] : prev.reportPages
        }));
      }
    };
    reader.readAsDataURL(file);
  }, []);

  const startCamera = useCallback(async (mode = null) => {
    try {
      // Check if running on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      
      // Check if HTTPS is required (camera needs secure context)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        alert('Camera access requires HTTPS. Please access this site via https:// or use localhost for testing.');
        return;
      }
      
      // Pre-set camera preview state before getting stream for better UX
      setCameraPreview(true);
      setCameraMode(mode);
      setCapturedPhoto(null);
      setCameraStarting(true);
      
      // Try different constraint strategies for better mobile compatibility
      let constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };
      
      let mediaStream;
      let constraintAttempts = [
        // Attempt 1: High-res with environment camera
        {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        },
        // Attempt 2: Lower resolution
        {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        },
        // Attempt 3: Basic video only
        {
          video: true,
          audio: false
        },
        // Attempt 4: iOS Safari specific - must have exact constraints
        {
          video: {
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 }
          },
          audio: false
        }
      ];
      
      let lastError;
      for (let i = 0; i < constraintAttempts.length; i++) {
        try {
          console.log(`Trying camera constraints attempt ${i + 1}:`, constraintAttempts[i]);
          mediaStream = await navigator.mediaDevices.getUserMedia(constraintAttempts[i]);
          console.log('Camera access successful with attempt', i + 1);
          break;
        } catch (err) {
          console.log(`Attempt ${i + 1} failed:`, err.name, err.message);
          lastError = err;
          if (i === constraintAttempts.length - 1) {
            throw lastError;
          }
        }
      }
      
      if (!mediaStream) {
        throw new Error('Could not get camera stream after all attempts');
      }
      
      setStream(mediaStream);
      setCameraStarting(false);
      
      // Wait for video element to be ready - longer delay for mobile, especially iOS
      const setupDelay = isIOS ? 800 : (isMobile ? 600 : 100);
      
      setTimeout(() => {
        if (videoRef.current) {
          const video = videoRef.current;
          
          // Critical: Set srcObject before any other operations
          video.srcObject = mediaStream;
          
          // Critical for iOS Safari - set all attributes
          video.playsInline = true;
          video.muted = true;
          video.autoplay = true;
          video.setAttribute('playsinline', 'true');
          video.setAttribute('webkit-playsinline', 'true');
          video.setAttribute('x5-playsinline', 'true'); // For WeChat browser
          video.setAttribute('muted', 'true');
          
          // Disable video mirroring for more natural view
          video.style.transform = 'scaleX(1)';
          
          // iOS specific: Ensure video is visible and loaded
          video.style.display = 'block';
          video.style.width = '100%';
          video.style.height = 'auto';
          
          // Force play with multiple attempts for mobile
          const playVideo = async () => {
            try {
              if (video.paused) {
                const playPromise = video.play();
                if (playPromise !== undefined) {
                  await playPromise;
                  console.log('Video playing successfully');
                }
              }
            } catch (err) {
              console.log('Play attempt failed:', err);
              // For iOS, we need user interaction
              if (isIOS) {
                // Show a tap-to-start overlay instead of alert
                console.log('iOS requires user interaction to start camera');
              }
            }
          };
          
          // Multiple play attempts with increasing delays
          playVideo();
          setTimeout(playVideo, 200);
          setTimeout(playVideo, 500);
          
          // Handle loadedmetadata event
          video.onloadedmetadata = () => {
            console.log('Video metadata loaded');
            playVideo();
          };
          
          // Handle canplay event
          video.oncanplay = () => {
            console.log('Video can play');
            playVideo();
          };
        }
      }, setupDelay);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraPreview(false); // Reset preview state on error
      setCameraStarting(false);
      
      let errorMsg = 'Could not access camera. ';
      if (error.name === 'NotAllowedError') {
        errorMsg += 'Camera permission was denied. Please check your browser settings and ensure camera access is allowed for this site.';
      } else if (error.name === 'NotFoundError') {
        errorMsg += 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMsg += 'Camera is already in use by another application. Please close other apps using the camera.';
      } else if (error.name === 'OverconstrainedError') {
        errorMsg += 'Camera constraints not supported. Please try a different device.';
      } else if (error.name === 'SecurityError') {
        errorMsg += 'Camera access blocked. This site must be served over HTTPS for camera access.';
      } else if (error.name === 'AbortError') {
        errorMsg += 'Camera access was aborted. Please try again.';
      } else if (error.name === 'TypeError') {
        errorMsg += 'Camera not supported on this browser. Please use Chrome or Safari.';
      } else {
        errorMsg += 'Error: ' + (error.message || 'Unknown error');
      }
      alert(errorMsg);
    }
  }, [setStream]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraPreview(false);
    setCameraMode(null);
    setCapturedPhoto(null);
    setCameraStarting(false);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream, setStream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !stream) {
      alert('Please start camera first');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedPhoto(imageData);
  }, [stream]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    // Video element stays mounted, just show it again by clearing capturedPhoto
  }, []);

  const confirmPhoto = useCallback(() => {
    if (!capturedPhoto) return;
    
    // Handle camera capture based on mode
    if (cameraMode === 'pm') {
      setForm(prev => ({
        ...prev,
        pmReportPreview: capturedPhoto,
        pmReportFile: null // Clear file since we're using camera capture
      }));
    } else if (cameraMode === 'cal') {
      setForm(prev => ({
        ...prev,
        calReportPreview: capturedPhoto,
        calReportFile: null // Clear file since we're using camera capture
      }));
    } else {
      // Single report mode
      setForm(prev => ({
        ...prev,
        reportPages: [...prev.reportPages, capturedPhoto],
        reportPreview: capturedPhoto
      }));
    }
    
    // Stop camera after confirming
    stopCamera();
    setCameraMode(null);
  }, [capturedPhoto, stopCamera, cameraMode]);

  const removePage = useCallback((index) => {
    setForm(prev => ({
      ...prev,
      reportPages: prev.reportPages.filter((_, i) => i !== index)
    }));
  }, []);

  const searchMatchingAssets = useCallback(() => {
    const searchTerm = form.equipmentType.toLowerCase().trim();
    if (!searchTerm) {
      alert('Please enter equipment type/name to search');
      return;
    }

    setIsLoading(true);
    
    // Use setTimeout to allow UI to update and show loading state
    setTimeout(() => {
      const matched = assets.filter(asset => {
        const equipmentName = String(asset.equipmentName || '').toLowerCase();
        const assetId = String(asset.assetId || '').toLowerCase();
        const serialNo = String(asset.serialNo || '').toLowerCase();
        const department = String(asset.department || '').toLowerCase();
        
        return equipmentName.includes(searchTerm) ||
               assetId.includes(searchTerm) ||
               serialNo.includes(searchTerm) ||
               department.includes(searchTerm);
      });

      setForm(prev => ({
        ...prev,
        matchedAssets: matched,
        assignToAll: false
      }));
      
      // Reset selected assets when new search is performed
      setSelectedAssetIds(new Set());
      
      // Only show first 50 assets initially for performance
      setVisibleAssets(matched.slice(0, 50));
      
      setIsLoading(false);

      if (matched.length === 0) {
        alert('No equipment found matching your search term');
      }
    }, 100);
  }, [form.equipmentType, assets]);

  const executeAssign = useCallback(() => {
    if (form.matchedAssets.length === 0) {
      alert('Please search for equipment first');
      return;
    }

    // Check files based on report type
    if (form.reportType === 'both') {
      if (!form.pmReportFile && !form.pmReportPreview) {
        alert('Please upload PM report');
        return;
      }
      if (!form.calReportFile && !form.calReportPreview) {
        alert('Please upload Calibration report');
        return;
      }
    } else {
      if (!form.reportFile && form.reportPages.length === 0) {
        alert('Please upload or capture a report');
        return;
      }
    }

    const targetAssets = form.assignToAll 
      ? form.matchedAssets 
      : form.matchedAssets.filter(asset => selectedAssetIds.has(getAssetKey(asset)));

    if (targetAssets.length === 0) {
      alert('Please select at least one equipment to assign the report');
      return;
    }

    if (!window.confirm(`Assign this report to ${targetAssets.length} equipment(s)?`)) {
      return;
    }

    // Handle both report types
    if (form.reportType === 'both') {
      onExecute({
        targetAssets,
        reportType: 'both',
        pmReportData: form.pmReportPreview,
        pmFileName: form.pmReportFile?.name || 'PM_Report.pdf',
        calReportData: form.calReportPreview,
        calFileName: form.calReportFile?.name || 'Calibration_Report.pdf'
      });
    } else {
      onExecute({
        targetAssets,
        reportData: form.reportPages.length > 0 
          ? JSON.stringify(form.reportPages)
          : form.reportPreview,
        reportType: form.reportType,
        pageCount: form.reportPages.length || 1
      });
    }
    // Note: onClose is called by the parent (handleSmartAssignExecute -> closeSmartAssign)
    // after saving. Do NOT call onClose here to avoid double-close.
  }, [form, selectedAssetIds, onExecute, getAssetKey]);

  const handleClose = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    onClose();
  }, [stream, setStream, onClose]);

  const loadMoreAssets = useCallback(() => {
    const currentLength = visibleAssets.length;
    const moreAssets = form.matchedAssets.slice(currentLength, currentLength + 50);
    setVisibleAssets(prev => [...prev, ...moreAssets]);
  }, [visibleAssets.length, form.matchedAssets]);

  if (!isOpen) return null;

  // Render using portal to prevent parent re-renders from affecting modal
  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-content smart-assign-modal">
        <div className="modal-header">
          <h3>Smart Multi-Equipment Assignment</h3>
          <button className="btn-close" onClick={handleClose}>×</button>
        </div>
        <div className="modal-body">
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <p>Loading...</p>
            </div>
          )}
          <div className="smart-assign-instructions">
            <h4>How it works:</h4>
            <p>Upload <strong>ONE service report</strong> and assign it to <strong>multiple equipment</strong> automatically.</p>
            <p className="example">Example: Upload "Syringe Pump Service Report" → Assign to ALL Syringe Pumps</p>
          </div>
          
          <div className="form-section">
            <h4>1. Select Report Type</h4>
            <div className="report-type-options">
              <label className={`radio-label ${form.reportType === 'pm' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="reportType"
                  value="pm"
                  checked={form.reportType === 'pm'}
                  onChange={(e) => setForm(prev => ({ ...prev, reportType: e.target.value }))}
                />
                <span>PM Report</span>
              </label>
              <label className={`radio-label ${form.reportType === 'cal' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="reportType"
                  value="cal"
                  checked={form.reportType === 'cal'}
                  onChange={(e) => setForm(prev => ({ ...prev, reportType: e.target.value }))}
                />
                <span>Calibration Report</span>
              </label>
              <label className={`radio-label ${form.reportType === 'both' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="reportType"
                  value="both"
                  checked={form.reportType === 'both'}
                  onChange={(e) => setForm(prev => ({ ...prev, reportType: e.target.value }))}
                />
                <span>Both (PM + Calibration)</span>
              </label>
            </div>
          </div>
          
          <div className="form-section">
            <h4>2. Upload or Capture Report</h4>
            
            {/* Dual upload for Both option */}
            {form.reportType === 'both' ? (
              <div className="dual-upload-section">
                {/* PM Report Upload */}
                <div className="upload-row">
                  <label className="upload-label">PM Report:</label>
                  <input
                    type="file"
                    id="smartAssignPmFile"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => handleFileChange(e.target.files[0], 'pm')}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="smartAssignPmFile" className="btn btn-primary">
                    {form.pmReportFile ? '✓ Change PM File' : 'Choose PM File'}
                  </label>
                  {!cameraPreview || cameraMode !== 'pm' ? (
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => startCamera('pm')}
                      disabled={cameraPreview && cameraMode !== 'pm'}
                    >
                      Camera
                    </button>
                  ) : (
                    <button 
                      type="button"
                      className="btn btn-danger"
                      onClick={stopCamera}
                    >
                      Stop Camera
                    </button>
                  )}
                  {form.pmReportFile && (
                    <span className="selected-file-name">{form.pmReportFile.name}</span>
                  )}
                  {form.pmReportPreview && !form.pmReportFile && (
                    <span className="selected-file-name">✓ PM Captured</span>
                  )}
                </div>
                
                {/* Calibration Report Upload */}
                <div className="upload-row">
                  <label className="upload-label">Calibration Report:</label>
                  <input
                    type="file"
                    id="smartAssignCalFile"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => handleFileChange(e.target.files[0], 'cal')}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="smartAssignCalFile" className="btn btn-primary">
                    {form.calReportFile ? '✓ Change Cal File' : 'Choose Cal File'}
                  </label>
                  {!cameraPreview || cameraMode !== 'cal' ? (
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => startCamera('cal')}
                      disabled={cameraPreview && cameraMode !== 'cal'}
                    >
                      Camera
                    </button>
                  ) : (
                    <button 
                      type="button"
                      className="btn btn-danger"
                      onClick={stopCamera}
                    >
                      Stop Camera
                    </button>
                  )}
                  {form.calReportFile && (
                    <span className="selected-file-name">{form.calReportFile.name}</span>
                  )}
                  {form.calReportPreview && !form.calReportFile && (
                    <span className="selected-file-name">✓ Cal Captured</span>
                  )}
                </div>
              </div>
            ) : (
              /* Single upload for PM or Cal */
              <div className="upload-options">
                <input
                  type="file"
                  id="smartAssignFile"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => handleFileChange(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <label htmlFor="smartAssignFile" className="btn btn-primary">
                  Choose File
                </label>
                {!cameraPreview ? (
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => startCamera()}
                  >
                    Start Camera
                  </button>
                ) : (
                  <button 
                    type="button"
                    className="btn btn-danger"
                    onClick={stopCamera}
                  >
                    Stop Camera
                  </button>
                )}
              </div>
            )}
            
            {/* Camera Preview Area */}
            {cameraPreview && (
              <div className="camera-preview-container">
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                
                {/* Camera Mode Label */}
                {cameraMode && (
                  <p className="camera-mode-label">
                    Capturing for: <strong>{cameraMode === 'pm' ? 'PM Report' : 'Calibration Report'}</strong>
                  </p>
                )}
                
                {/* Live Camera View - Always rendered but hidden when photo captured */}
                <div 
                  className="camera-live-view" 
                  style={{ display: capturedPhoto ? 'none' : 'flex', position: 'relative' }}
                >
                  {/* iOS Tap to Start Overlay */}
                  {cameraStarting && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        color: 'white',
                        fontSize: '1.2rem',
                        textAlign: 'center',
                        padding: '20px'
                      }}
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.play().catch(() => {});
                        }
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📷</div>
                        <div>Tap to start camera</div>
                        <div style={{ fontSize: '0.8rem', marginTop: '10px', opacity: 0.8 }}>
                          Starting camera...
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <video 
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    controls={false}
                    className="camera-video"
                    style={{ 
                      width: '100%', 
                      maxWidth: '640px',
                      transform: 'scaleX(1)', // Prevent mirror effect on some devices
                      objectFit: 'cover'
                    }}
                    onClick={() => {
                      if (videoRef.current && videoRef.current.paused) {
                        videoRef.current.play().catch(() => {});
                      }
                    }}
                    onLoadedMetadata={(e) => {
                      e.target.play().catch(err => {
                        console.log('Auto-play prevented on loadedmetadata:', err);
                      });
                    }}
                    onCanPlay={(e) => {
                      e.target.play().catch(err => {
                        console.log('Auto-play prevented on canplay:', err);
                      });
                    }}
                    onError={(e) => {
                      console.error('Video error:', e);
                      alert('Camera video error. Please try again or use file upload instead.');
                    }}
                  />
                  <button 
                    type="button"
                    className="btn btn-success capture-btn"
                    onClick={capturePhoto}
                    disabled={cameraStarting}
                  >
                    {cameraStarting ? 'Starting...' : 'Capture Photo'}
                  </button>
                </div>
                
                {/* Captured Photo Review - Hidden until photo captured */}
                <div 
                  className="captured-photo-review"
                  style={{ display: capturedPhoto ? 'flex' : 'none' }}
                >
                  <p className="review-label">Preview:</p>
                  {capturedPhoto && (
                    <img 
                      src={capturedPhoto} 
                      alt="Captured" 
                      className="captured-image-preview"
                    />
                  )}
                  <div className="photo-actions">
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      onClick={retakePhoto}
                    >
                      Retake Photo
                    </button>
                    <button 
                      type="button"
                      className="btn btn-success"
                      onClick={confirmPhoto}
                      disabled={!capturedPhoto}
                    >
                      Confirm & Add
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {form.reportFile && (
              <p className="selected-file">Selected: {form.reportFile.name}</p>
            )}
            
            {form.reportPages.length > 0 && (
              <div className="pages-container">
                <p className="pages-title">Captured Pages ({form.reportPages.length}):</p>
                <div className="pages-grid">
                  {form.reportPages.map((page, index) => (
                    <div key={index} className="page-item">
                      <img src={page} alt={`Page ${index + 1}`} className="page-thumbnail" />
                      <div className="page-overlay">
                        <span className="page-number">{index + 1}</span>
                        <button 
                          className="btn-remove-page"
                          onClick={(e) => { e.stopPropagation(); removePage(index); }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="form-section">
            <h4>3. Find Equipment</h4>
            <div className="search-row">
              <input
                type="text"
                placeholder="Enter equipment type (e.g., Syringe Pump, Monitor, etc.)"
                value={form.equipmentType}
                onChange={(e) => setForm(prev => ({ ...prev, equipmentType: e.target.value }))}
                className="search-input"
              />
              <button className="btn btn-primary" onClick={searchMatchingAssets}>
                Search
              </button>
            </div>
            
            {form.matchedAssets.length > 0 && (
              <div className="matched-assets-section">
                <div className="matched-header">
                  <h5>Found {form.matchedAssets.length} Equipment:</h5>
                  <label className="select-all-label">
                    <input
                      type="checkbox"
                      checked={form.assignToAll}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setForm(prev => ({ ...prev, assignToAll: isChecked }));
                        // When Select All is toggled, update all selectedAssetIds
                        if (isChecked) {
                          setSelectedAssetIds(new Set(form.matchedAssets.map(a => getAssetKey(a))));
                        } else {
                          setSelectedAssetIds(new Set());
                        }
                      }}
                    />
                    <span>Select All</span>
                  </label>
                </div>
                <div className="assets-list">
                  {visibleAssets.map((asset) => (
                    <div key={getAssetKey(asset)} className="asset-checkbox-item">
                      <input
                        type="checkbox"
                        id={`asset-check-${getAssetKey(asset)}`}
                        checked={selectedAssetIds.has(getAssetKey(asset))}
                        onChange={(e) => {
                          const newSelected = new Set(selectedAssetIds);
                          const assetKey = getAssetKey(asset);
                          if (e.target.checked) {
                            newSelected.add(assetKey);
                          } else {
                            newSelected.delete(assetKey);
                            // Uncheck "Select All" if any item is unchecked
                            if (form.assignToAll) {
                              setForm(prev => ({ ...prev, assignToAll: false }));
                            }
                          }
                          setSelectedAssetIds(newSelected);
                        }}
                      />
                      <label htmlFor={`asset-check-${getAssetKey(asset)}`}>
                        <strong>{asset.equipmentName}</strong>
                        <span className="asset-meta">
                          (Asset: {asset.assetId || 'N/A'} | Dept: {asset.department || 'N/A'})
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                {visibleAssets.length < form.matchedAssets.length && (
                  <button 
                    className="btn btn-secondary load-more-btn" 
                    onClick={loadMoreAssets}
                  >
                    Load More ({form.matchedAssets.length - visibleAssets.length} remaining)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            className="btn btn-success" 
            onClick={executeAssign}
            disabled={form.matchedAssets.length === 0 || 
              (!form.assignToAll && selectedAssetIds.size === 0) ||
              (form.reportType === 'both' 
                ? (!form.pmReportFile && !form.pmReportPreview) || (!form.calReportFile && !form.calReportPreview)
                : (!form.reportFile && form.reportPages.length === 0))}
          >
            Assign to Selected Equipment
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(SmartAssignModal);
